// Third Party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import {
  createNewScenario,
  setIncomeStreamCapital,
  saveScenario,
  removeScenario,
  loadScenarioForEdit,
  cancelScenarioEditing,
  updateScenario,
  updateComparisonItemColor,
} from '../../../../redux/modules/comparison';

// Components
import IncomeStreamTableLabels from './IncomeStreamTableLabels';
import IncomeStreamCard from './IncomeStreamCard';
import ComparisonScrollbarContainer from '../misc/ComparisonScrollbarContainer';

// Utils
import { getDataYears, EDIT_SCENARIO_CARD_WIDTH, filterIncomeStreamItems } from '../../../../utils/income-stream';
import ComparisonItem from '@/types/comparison-item';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import Portfolio from '@/types/portfolio';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import Comparison from '@/types/comparison';

type DispatchProps = {
  createNewScenario: (item: ComparisonItem) => void;
  setIncomeStreamCapital: (item: ComparisonItem, capital: number, startDate: Date, endDate: Date) => void;
  saveScenario: (item: ComparisonItem, startDate: Date) => void;
  removeScenario: (item: ComparisonItem, scenario: IncomeStreamScenario) => void;
  loadScenarioForEdit: (scenario: IncomeStreamScenario) => void;
  cancelScenarioEditing: (item: ComparisonItem) => void;
  updateScenario: (scenario: IncomeStreamScenario) => void;
  onSavePortfolio: (p: Portfolio) => Promise<Portfolio>;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
};

type StateProps = {
  items: ComparisonItem[];
  user?: Nullable<User>;
  editedScenario?: IncomeStreamScenario;
  editedPortfolio: Portfolio;
  editedComparisonItem?: ComparisonItem;
  readOnly?: boolean;
  isEditPinned: boolean;
  comparison: Comparison;
};

type Props = StateProps &
  DispatchProps & {
    startDate: Date;
    endDate: Date;
    popoverContainerRef: Nullable<HTMLDivElement>;
  };

class IncomeStreamTable extends Component<Props> {
  private incomeStreamTableRef = React.createRef<HTMLDivElement>();

  private comparisonScrollbarContainerRef: ComparisonScrollbarContainer | null = null;

  focusScenarioEdit = () => {
    const cardsContainer = this.getScrollContainerRef();
    const target = document.getElementsByClassName('distribution-scenario editing-mode')[0] as HTMLElement;

    if (target.parentElement && cardsContainer && cardsContainer.scrollTo) {
      const leftEdge = target.offsetLeft + target.parentElement.offsetLeft;

      if (leftEdge < cardsContainer.clientWidth - EDIT_SCENARIO_CARD_WIDTH - 20) {
        return;
      }

      setTimeout(() => {
        cardsContainer.scrollTo({
          left: leftEdge - 20,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  itemIsBeingEdited = (item: ComparisonItem) => !!this.props.editedPortfolio._id && this.props.editedPortfolio._id === item._id;

  renderCard = (item: ComparisonItem, years: string[]) => {
    const { editedScenario, startDate, endDate, editedPortfolio, readOnly, isEditPinned, editedComparisonItem, user, comparison } = this.props;

    const isEdited = this.itemIsBeingEdited(item);

    const editMode = !isEditPinned && isEdited;

    return (
      <IncomeStreamCard
        key={item._id}
        item={(isEdited && editedComparisonItem) || item}
        user={user}
        years={years}
        startDate={startDate}
        endDate={endDate}
        createNewScenario={this.props.createNewScenario}
        saveScenario={this.props.saveScenario}
        removeScenario={this.props.removeScenario}
        setIncomeStreamCapital={this.props.setIncomeStreamCapital}
        editedScenario={editedScenario}
        loadScenarioForEdit={this.props.loadScenarioForEdit}
        cancelScenarioEditing={this.props.cancelScenarioEditing}
        updateScenario={this.props.updateScenario}
        editedPortfolio={editedPortfolio}
        onSavePortfolio={this.props.onSavePortfolio}
        readOnly={readOnly}
        editMode={editMode}
        onModifyPortfolio={this.props.onModifyPortfolio}
        onClonePortfolio={this.props.onClonePortfolio}
        onItemRemove={this.props.onItemRemove}
        isEditPinned={isEditPinned}
        isEdited={isEdited}
        comparison={comparison}
        updateComparisonItemColor={this.props.updateComparisonItemColor}
        popoverContainerRef={this.props.popoverContainerRef}
        scrollbarContainerRef={this.getScrollContainerRef()}
      />
    );
  };

  getScrollContainerRef = () => (this.comparisonScrollbarContainerRef ? this.comparisonScrollbarContainerRef.getScrollContainerRef() : undefined);

  getTablePaddingRef = () => {
    if (!this.comparisonScrollbarContainerRef) {
      return undefined;
    }

    return this.comparisonScrollbarContainerRef.getPaddingRef() || undefined;
  };

  onScrollX = (atStart?: boolean) => {
    if (!this.incomeStreamTableRef.current) {
      return;
    }

    if (atStart) {
      this.incomeStreamTableRef.current.classList.remove('is-scrolled-x');
    } else {
      this.incomeStreamTableRef.current.classList.add('is-scrolled-x');
    }
  };

  render() {
    const { items, editedComparisonItem, isEditPinned, editedScenario } = this.props;

    const years = getDataYears(items);
    const tableClasses = classNames('income-stream-table d-flex');

    return (
      <div className={tableClasses} ref={this.incomeStreamTableRef}>
        <IncomeStreamTableLabels
          items={items}
          editedComparisonItem={editedComparisonItem}
          years={years}
          scrollContainerRef={this.getScrollContainerRef()}
          tablePaddingRef={this.getTablePaddingRef()}
          editedIncomeStreamScenario={editedScenario}
          isEditPinned={isEditPinned}
        />

        <ComparisonScrollbarContainer
          className="income-stream-cards"
          isEditPinned={isEditPinned}
          withPadding
          onScrollX={this.onScrollX}
          ref={ref => {
            this.comparisonScrollbarContainerRef = ref;
          }}
        >
          {filterIncomeStreamItems(items).map(i => this.renderCard(i, years))}
        </ComparisonScrollbarContainer>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  editedPortfolio: state.editPortfolio.portfolio,
  editedComparisonItem: state.editPortfolio.comparisonItem,
  user: state.auth.user,
  editedScenario: state.comparison.editedScenario,
});

const mapDispatchToProps = {
  createNewScenario,
  setIncomeStreamCapital,
  saveScenario,
  removeScenario,
  loadScenarioForEdit,
  cancelScenarioEditing,
  updateScenario,
  updateComparisonItemColor,
};

export default connect(mapStateToProps, mapDispatchToProps)(IncomeStreamTable);
